import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, useEffect, useState } from 'react';
// lazy do wrapper local — recupera "Failed to fetch dynamically imported
// module" pós-deploy (chunk antigo sumiu do S3). Ver utils/lazyWithRetry.ts.
import { lazy } from './utils/lazyWithRetry';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { setupApiLoadingInterceptors } from './services/apiLoadingConfig';
import { configApi } from './services/api';
import { PublicConfigContext, type PublicConfig } from './contexts/PublicConfigContext';
import { I18N_ROUTING_ENABLED, type AppLocale } from './i18n';
import { localePrefix, localizedPath, ROUTE_SLUGS } from './i18n/slugMap';

// ─── Eager (sempre carregadas no main bundle) ────────────────────────────────
// Landing e AuthPage sao as rotas mais comuns de aterrissagem direta — manter
// eager evita um chunk extra no caminho critico da primeira visita.
import { Landing } from './pages/Landing';
import { AuthPage } from './pages/AuthPage';

// ─── Lazy (cada rota vira chunk separado, baixado sob demanda) ───────────────

// Auth flows
const CompleteRegistration = lazy(() =>
  import('./pages/CompleteRegistration').then(m => ({ default: m.CompleteRegistration }))
);
const ForgotPassword = lazy(() =>
  import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword }))
);
const ResetPassword = lazy(() =>
  import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword }))
);
const VerifyEmailSent = lazy(() =>
  import('./pages/VerifyEmailSent').then(m => ({ default: m.VerifyEmailSent }))
);
const ActivateAccount = lazy(() =>
  import('./pages/ActivateAccount').then(m => ({ default: m.ActivateAccount }))
);
const JoinInvite = lazy(() => import('./pages/JoinInvite'));
const ExcluirConta = lazy(() => import('./pages/ExcluirConta'));

// Institucionais
const TermosDeUso = lazy(() => import('./pages/TermosDeUso'));
const Regulamento = lazy(() => import('./pages/Regulamento'));
const Privacidade = lazy(() => import('./pages/Privacidade'));
const Sobre = lazy(() => import('./pages/Sobre'));
const Contato = lazy(() => import('./pages/Contato'));
const ComoJogar = lazy(() => import('./pages/ComoJogar'));
const Glossario = lazy(() => import('./pages/Glossario'));
const Premium = lazy(() => import('./pages/Premium'));
const Precos = lazy(() => import('./pages/Precos'));

// Area autenticada (usuario comum)
const Home = lazy(() => import('./pages/Home'));
const MyAccount = lazy(() =>
  import('./pages/MyAccount').then(m => ({ default: m.MyAccount }))
);
const PoolMatches = lazy(() => import('./pages/PoolMatches'));
const TodayHub = lazy(() => import('./pages/TodayHub'));
const PoolRankings = lazy(() => import('./pages/PoolRankings'));
const PublicPools = lazy(() => import('./pages/PublicPools'));
const GlobalRanking = lazy(() => import('./pages/GlobalRanking'));
const MyAchievements = lazy(() => import('./pages/MyAchievements'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Statistics = lazy(() => import('./pages/Statistics'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));

// Admin
const Users = lazy(() => import('./pages/Users'));
const Competitions = lazy(() => import('./pages/Competitions'));
const CompetitionMatches = lazy(() => import('./pages/CompetitionMatches'));
const NotifyUsers = lazy(() => import('./pages/NotifyUsers'));
const NotificationLogsAdmin = lazy(() => import('./pages/NotificationLogsAdmin'));
const Configuracoes = lazy(() =>
  import('./pages/Configuracoes').then(m => ({ default: m.Configuracoes }))
);
const CouponsAdmin = lazy(() => import('./pages/CouponsAdmin'));
const SubscriptionsAdmin = lazy(() => import('./pages/SubscriptionsAdmin'));
const WebhooksAdmin = lazy(() => import('./pages/WebhooksAdmin'));
const TeamsTranslationAdmin = lazy(() => import('./pages/TeamsTranslationAdmin'));
const TranslationsAdmin = lazy(() => import('./pages/TranslationsAdmin'));
const Jogadores = lazy(() => import('./pages/Jogadores'));

// Blog editorial (publico, prerendered no build)
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));

// Landing pages SEO (publicas, prerendered no build)
const LandingBrasileiro = lazy(() => import('./pages/LandingBrasileiro'));
const LandingLibertadores = lazy(() => import('./pages/LandingLibertadores'));
const LandingChampions = lazy(() => import('./pages/LandingChampions'));
const LandingCopaMundo = lazy(() => import('./pages/LandingCopaMundo'));
const LandingCopaDoBrasil = lazy(() => import('./pages/LandingCopaDoBrasil'));
const LandingCarioca = lazy(() => import('./pages/LandingCarioca'));
const LandingPaulista = lazy(() => import('./pages/LandingPaulista'));
const LandingEstaduais = lazy(() => import('./pages/LandingEstaduais'));
const LandingSimuladorCopaMundo = lazy(() => import('./pages/LandingSimuladorCopaMundo'));
const LandingSimuladorLibertadores = lazy(() => import('./pages/LandingSimuladorLibertadores'));
const LandingSimuladorSudamericana = lazy(() => import('./pages/LandingSimuladorSudamericana'));
const LandingEscalarSelecao = lazy(() =>
  import('./pages/LandingEscalarSelecao').then(m => ({ default: m.LandingEscalarSelecao }))
);
const LandingEscalarTime = lazy(() => import('./pages/LandingEscalarTime'));

// Embed routes — widgets sem header/footer/ads pra iframe em sites de terceiros
const EmbedSimuladorCopaMundo = lazy(() => import('./pages/embed/EmbedSimuladorCopaMundo'));
const EmbedSimuladorLibertadores = lazy(() => import('./pages/embed/EmbedSimuladorLibertadores'));
const EmbedSimuladorSudamericana = lazy(() => import('./pages/embed/EmbedSimuladorSudamericana'));
const EmbedEscalarSelecao = lazy(() => import('./pages/embed/EmbedEscalarSelecao'));
const EmbedEscalarTime = lazy(() => import('./pages/embed/EmbedEscalarTime'));

export { usePublicConfig } from './contexts/PublicConfigContext';

// ─── Rotas públicas localizáveis ─────────────────────────────────────────────
// Mapa routeId (de ROUTE_SLUGS) → elemento. Usado pra gerar as rotas /en/* e
// /es/* — os MESMOS componentes das rotas PT-BR, em URLs com slug traduzido.
// PT-BR continua nas rotas explícitas sem prefixo (zero regressão).
const PUBLIC_ROUTE_ELEMENTS: Record<string, React.ReactElement> = {
  home: <Landing />,
  'pool-copa-do-mundo': <LandingCopaMundo />,
  'pool-brasileirao': <LandingBrasileiro />,
  'pool-copa-do-brasil': <LandingCopaDoBrasil />,
  'pool-libertadores': <LandingLibertadores />,
  'pool-champions-league': <LandingChampions />,
  'pool-carioca': <LandingCarioca />,
  'pool-paulista': <LandingPaulista />,
  'pool-estaduais': <LandingEstaduais />,
  'sim-copa-do-mundo': <LandingSimuladorCopaMundo />,
  'sim-libertadores': <LandingSimuladorLibertadores />,
  'sim-sudamericana': <LandingSimuladorSudamericana />,
  'tool-escalar-selecao': <LandingEscalarSelecao />,
  'tool-escale-seu-time': <LandingEscalarTime />,
  sobre: <Sobre />,
  contato: <Contato />,
  'como-jogar': <ComoJogar />,
  glossario: <Glossario />,
  premium: <Premium />,
  precos: <Precos />,
  'termos-de-uso': <TermosDeUso />,
  privacidade: <Privacidade />,
  regulamento: <Regulamento />,
  'excluir-conta': <ExcluirConta />,
  blog: <Blog />,
};

// ─── Rotas autenticadas (SPA) localizáveis ──────────────────────────────────
// Mapa routeId → elemento (já envolto em ProtectedRoute com roles certas).
// Diferente de PUBLIC_ROUTE_ELEMENTS, estas rotas:
//  - NÃO são prerendered (são SPA dinâmicas atrás de auth)
//  - O fallback /index.html da CloudFront Function cobre o GET inicial
//  - Auth + onboarding (`auth`, `forgot-password`, etc.) também entram aqui
//    pra ter URLs `/en/auth`, `/es/auth` quando o user troca idioma na tela
//    de login
const AUTHENTICATED_ROUTE_ELEMENTS: Record<string, React.ReactElement> = {
  // Páginas pós-login (todos os usuários)
  boloes: <ProtectedRoute><Home /></ProtectedRoute>,
  'jogos-hoje': <ProtectedRoute><TodayHub /></ProtectedRoute>,
  'boloes-publicos': <ProtectedRoute><PublicPools /></ProtectedRoute>,
  'ranking-global': <ProtectedRoute><GlobalRanking /></ProtectedRoute>,
  'minhas-conquistas': <ProtectedRoute><MyAchievements /></ProtectedRoute>,
  estatisticas: <ProtectedRoute><Statistics /></ProtectedRoute>,
  notificacoes: <ProtectedRoute><Notifications /></ProtectedRoute>,
  'minha-conta': <ProtectedRoute><MyAccount /></ProtectedRoute>,
  assinatura: <ProtectedRoute><SubscriptionPage /></ProtectedRoute>,
  jogadores: <ProtectedRoute><Jogadores /></ProtectedRoute>,

  // Admin (ADMIN ou SUPER_ADMIN)
  cupons: <ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}><CouponsAdmin /></ProtectedRoute>,
  'notificar-usuarios': <ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}><NotifyUsers /></ProtectedRoute>,

  // Super admin
  usuarios: <ProtectedRoute requiredRoles={['SUPER_ADMIN']}><Users /></ProtectedRoute>,
  configuracoes: <ProtectedRoute requiredRoles={['SUPER_ADMIN']}><Configuracoes /></ProtectedRoute>,

  // Auth e onboarding (não protegidas — telas para deslogados)
  auth: <AuthPage />,
  'completar-registro': <CompleteRegistration />,
  'verificar-email': <VerifyEmailSent />,
  'ativar-conta': <ActivateAccount />,
  'forgot-password': <ForgotPassword />,
  'reset-password': <ResetPassword />,
  join: <JoinInvite />,
};

/**
 * Gera as rotas /en/<slug> e /es/<slug> para a área autenticada. Para rotas
 * dinâmicas (pools/:poolId/matches, competicoes/:competitionId/partidas,
 * admin/<sub>), também monta os paths localizados com :poolId/:competitionId
 * preservados, usando os ids `*-base` e `*-sub-*` de ROUTE_SLUGS.
 */
function buildAuthRoutesForLocale(loc: AppLocale): React.ReactElement[] {
  const routes: React.ReactElement[] = [];

  // Top-level (slug único)
  for (const [id, element] of Object.entries(AUTHENTICATED_ROUTE_ELEMENTS)) {
    routes.push(
      <Route key={`${loc}:${id}`} path={localizedPath(id, loc)} element={element} />,
    );
  }

  const prefix = localePrefix(loc);

  // Dinâmicas: /pools/:poolId/matches e /pools/:poolId/rankings
  const poolsBase = ROUTE_SLUGS['pools-base']?.[loc] ?? 'pools';
  const poolsSubMatches = ROUTE_SLUGS['pools-sub-matches']?.[loc] ?? 'matches';
  const poolsSubRankings = ROUTE_SLUGS['pools-sub-rankings']?.[loc] ?? 'rankings';
  routes.push(
    <Route
      key={`${loc}:pools-matches`}
      path={`${prefix}/${poolsBase}/:poolId/${poolsSubMatches}`}
      element={<ProtectedRoute><PoolMatches /></ProtectedRoute>}
    />,
    <Route
      key={`${loc}:pools-rankings`}
      path={`${prefix}/${poolsBase}/:poolId/${poolsSubRankings}`}
      element={<ProtectedRoute><PoolRankings /></ProtectedRoute>}
    />,
  );

  // Dinâmicas: /competicoes (listagem) e /competicoes/:competitionId/partidas
  const compsBase = ROUTE_SLUGS['competicoes-base']?.[loc] ?? 'competicoes';
  const compsSubPartidas = ROUTE_SLUGS['competicoes-sub-partidas']?.[loc] ?? 'partidas';
  routes.push(
    <Route
      key={`${loc}:competicoes`}
      path={`${prefix}/${compsBase}`}
      element={<ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}><Competitions /></ProtectedRoute>}
    />,
    <Route
      key={`${loc}:competicoes-partidas`}
      path={`${prefix}/${compsBase}/:competitionId/${compsSubPartidas}`}
      element={<ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}><CompetitionMatches /></ProtectedRoute>}
    />,
  );

  // Admin sub-rotas: /admin/notificacoes, /admin/assinaturas, /admin/times, /admin/traducoes
  const adminBase = ROUTE_SLUGS['admin-base']?.[loc] ?? 'admin';
  const adminSubNotif = ROUTE_SLUGS['admin-sub-notificacoes']?.[loc] ?? 'notificacoes';
  const adminSubAssin = ROUTE_SLUGS['admin-sub-assinaturas']?.[loc] ?? 'assinaturas';
  const adminSubTimes = ROUTE_SLUGS['admin-sub-times']?.[loc] ?? 'times';
  const adminSubTrad = ROUTE_SLUGS['admin-sub-traducoes']?.[loc] ?? 'traducoes';
  const adminSubWebhooks = ROUTE_SLUGS['admin-sub-webhooks']?.[loc] ?? 'webhooks';
  routes.push(
    <Route
      key={`${loc}:admin-notif`}
      path={`${prefix}/${adminBase}/${adminSubNotif}`}
      element={<ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}><NotificationLogsAdmin /></ProtectedRoute>}
    />,
    <Route
      key={`${loc}:admin-assin`}
      path={`${prefix}/${adminBase}/${adminSubAssin}`}
      element={<ProtectedRoute requiredRoles={['SUPER_ADMIN']}><SubscriptionsAdmin /></ProtectedRoute>}
    />,
    <Route
      key={`${loc}:admin-times`}
      path={`${prefix}/${adminBase}/${adminSubTimes}`}
      element={<ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}><TeamsTranslationAdmin /></ProtectedRoute>}
    />,
    <Route
      key={`${loc}:admin-trad`}
      path={`${prefix}/${adminBase}/${adminSubTrad}`}
      element={<ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}><TranslationsAdmin /></ProtectedRoute>}
    />,
    <Route
      key={`${loc}:admin-webhooks`}
      path={`${prefix}/${adminBase}/${adminSubWebhooks}`}
      element={<ProtectedRoute requiredRoles={['SUPER_ADMIN']}><WebhooksAdmin /></ProtectedRoute>}
    />,
  );

  return routes;
}

// ─── Suspense fallback ───────────────────────────────────────────────────────
// Renderizado enquanto o chunk lazy de uma rota esta sendo baixado. Mantem o
// background roxo do brand para evitar flash branco durante a transicao.
function SuspenseFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 to-purple-700">
      <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin" />
    </div>
  );
}

function AppContent() {
  const { showLoading, hideLoading } = useLoading();

  useEffect(() => {
    setupApiLoadingInterceptors(showLoading, hideLoading);
  }, [showLoading, hideLoading]);

  return (
    <>
      <LoadingSpinner />
      <CookieConsentBanner />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '500',
          },
          success: {
            iconTheme: { primary: '#10b981', secondary: '#fff' },
            style: { background: '#059669' },
          },
          error: {
            iconTheme: { primary: '#ef4444', secondary: '#fff' },
            style: { background: '#dc2626' },
          },
        }}
      />
      <Suspense fallback={<SuspenseFallback />}>
        <Routes>
          {/* Landing page pública */}
          <Route path="/" element={<Landing />} />

          {/* Rota principal de autenticação */}
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/completar-registro" element={<CompleteRegistration />} />

          {/* Rotas legadas — redirecionam para /auth */}
          <Route path="/login" element={<Navigate to="/auth" replace />} />
          <Route path="/register" element={<Navigate to="/auth" replace />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verificar-email" element={<VerifyEmailSent />} />
          <Route path="/ativar-conta" element={<ActivateAccount />} />
          <Route path="/join" element={<JoinInvite />} />
          <Route path="/termos-de-uso" element={<TermosDeUso />} />
          <Route path="/regulamento" element={<Regulamento />} />
          <Route path="/excluir-conta" element={<ExcluirConta />} />
          <Route path="/privacidade" element={<Privacidade />} />
          <Route path="/sobre" element={<Sobre />} />
          <Route path="/contato" element={<Contato />} />
          <Route path="/como-jogar" element={<ComoJogar />} />
          <Route path="/glossario" element={<Glossario />} />
          <Route path="/premium" element={<Premium />} />
          <Route path="/precos" element={<Precos />} />

          {/* Rotas protegidas */}
          <Route path="/boloes" element={<ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path="/jogos-hoje" element={<ProtectedRoute><TodayHub /></ProtectedRoute>} />
          <Route path="/minha-conta" element={<ProtectedRoute><MyAccount /></ProtectedRoute>} />
          <Route path="/pools/:poolId/matches" element={<ProtectedRoute><PoolMatches /></ProtectedRoute>} />
          <Route path="/pools/:poolId/rankings" element={<ProtectedRoute><PoolRankings /></ProtectedRoute>} />
          <Route path="/usuarios" element={<ProtectedRoute requiredRoles={['SUPER_ADMIN']}><Users /></ProtectedRoute>} />
          <Route path="/competicoes" element={<ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}><Competitions /></ProtectedRoute>} />
          <Route path="/competicoes/:competitionId/partidas" element={<ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}><CompetitionMatches /></ProtectedRoute>} />
          <Route path="/notificar-usuarios" element={<ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}><NotifyUsers /></ProtectedRoute>} />
          <Route path="/admin/notificacoes" element={<ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}><NotificationLogsAdmin /></ProtectedRoute>} />
          <Route path="/configuracoes" element={<ProtectedRoute requiredRoles={['SUPER_ADMIN']}><Configuracoes /></ProtectedRoute>} />
          <Route path="/boloes-publicos" element={<ProtectedRoute><PublicPools /></ProtectedRoute>} />
          <Route path="/ranking-global" element={<ProtectedRoute><GlobalRanking /></ProtectedRoute>} />
          <Route path="/minhas-conquistas" element={<ProtectedRoute><MyAchievements /></ProtectedRoute>} />
          <Route path="/notificacoes" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
          <Route path="/estatisticas" element={<ProtectedRoute><Statistics /></ProtectedRoute>} />
          <Route path="/cupons" element={<ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}><CouponsAdmin /></ProtectedRoute>} />
          <Route path="/assinatura" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
          <Route path="/admin/assinaturas" element={<ProtectedRoute requiredRoles={['SUPER_ADMIN']}><SubscriptionsAdmin /></ProtectedRoute>} />
          <Route path="/admin/webhooks" element={<ProtectedRoute requiredRoles={['SUPER_ADMIN']}><WebhooksAdmin /></ProtectedRoute>} />
          <Route path="/admin/times" element={<ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}><TeamsTranslationAdmin /></ProtectedRoute>} />
          <Route path="/admin/traducoes" element={<ProtectedRoute requiredRoles={['ADMIN', 'SUPER_ADMIN']}><TranslationsAdmin /></ProtectedRoute>} />

          {/* Landing Pages SEO — públicas, sem autenticação */}
          <Route path="/bolao-brasileirao" element={<LandingBrasileiro />} />
          <Route path="/bolao-libertadores" element={<LandingLibertadores />} />
          <Route path="/bolao-champions-league" element={<LandingChampions />} />
          <Route path="/bolao-copa-do-mundo" element={<LandingCopaMundo />} />
          <Route path="/bolao-copa-do-brasil" element={<LandingCopaDoBrasil />} />
          <Route path="/bolao-carioca" element={<LandingCarioca />} />
          <Route path="/bolao-paulista" element={<LandingPaulista />} />
          <Route path="/bolao-estaduais" element={<LandingEstaduais />} />

          {/* Landing Pages SEO — Simuladores de Copas */}
          <Route path="/simulador-copa-do-mundo-2026" element={<LandingSimuladorCopaMundo />} />
          <Route path="/simulador-libertadores-2026" element={<LandingSimuladorLibertadores />} />
          <Route path="/simulador-copa-sudamericana-2026" element={<LandingSimuladorSudamericana />} />

          {/* Ferramentas interativas — públicas, sem autenticação */}
          <Route path="/escalar-selecao-brasileira-2026" element={<LandingEscalarSelecao />} />
          <Route path="/escale-seu-time" element={<LandingEscalarTime />} />

          {/* Blog editorial — público, sem autenticação */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />

          {/* Rotas localizadas EN/ES — mesmos componentes, slugs traduzidos.
              Atrás da flag I18N_ROUTING_ENABLED; PT-BR (acima) fica intacto.
              Inclui rotas públicas (prerendered) E rotas autenticadas (SPA). */}
          {I18N_ROUTING_ENABLED &&
            (['en', 'es'] as const).flatMap((loc) => [
              // Rotas públicas (com prerender)
              ...Object.entries(PUBLIC_ROUTE_ELEMENTS).map(([id, element]) => (
                <Route key={`${loc}:${id}`} path={localizedPath(id, loc)} element={element} />
              )),
              <Route
                key={`${loc}:blogpost`}
                path={`${localizedPath('blog', loc)}/:slug`}
                element={<BlogPost />}
              />,
              // Rotas autenticadas (SPA — sem prerender; fallback /index.html cobre)
              ...buildAuthRoutesForLocale(loc),
            ])}

          {/* Embed routes — widgets pra iframe em sites de terceiros (noindex) */}
          <Route path="/embed/simulador-copa-do-mundo-2026" element={<EmbedSimuladorCopaMundo />} />
          <Route path="/embed/simulador-libertadores-2026" element={<EmbedSimuladorLibertadores />} />
          <Route path="/embed/simulador-copa-sudamericana-2026" element={<EmbedSimuladorSudamericana />} />
          <Route path="/embed/escalar-selecao-brasileira-2026" element={<EmbedEscalarSelecao />} />
          <Route path="/embed/escale-seu-time" element={<EmbedEscalarTime />} />

          {/* Área admin — jogadores */}
          <Route path="/jogadores" element={<ProtectedRoute><Jogadores /></ProtectedRoute>} />

          {/* Rota padrão - redirecionar para landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  const [publicConfig, setPublicConfig] = useState<PublicConfig>({
    googleVerificationCode: '',
    googleAnalyticsId: '',
    googleClientId: '',
  });

  // Carregar configurações públicas (Search Console, Analytics, Google Client ID)
  useEffect(() => {
    configApi.getPublic()
      .then(setPublicConfig)
      .catch(() => { /* silencioso: funcionalidades Google ficam desabilitadas */ });
  }, []);

  // GA4: tag inline no <head> do index.html dispara page_view no 1º load.
  // Aqui só seguramos o `publicConfig.googleAnalyticsId` no context pra features
  // que dependem de saber se GA está ativo. Não injeta mais via useEffect —
  // a injeção dinâmica falhava silenciosamente (gtag.js carregava mas hits
  // /g/collect não saíam; validado via DevTools Network).

  return (
    <PublicConfigContext.Provider value={publicConfig}>
      <BrowserRouter>
        <LoadingProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </LoadingProvider>
      </BrowserRouter>
    </PublicConfigContext.Provider>
  );
}

export default App;
